import { ChordLib } from '@tunasong/music-lib'
import type { Chord, EventDuration, NoteEvent, Octave } from '@tunasong/music-lib'
import type { MelodyNote, NoteName } from '@tunasong/schemas'
import { usePlayNotes } from '../player/piano-player.hook.js'

const BEATS_PER_CHORD = 4
const CHORD_VOICING_OCTAVE = 3
const MELODY_OCTAVE_FALLBACK = 4

/**
 * Internal playback event — time-aligned, track-tagged. Convenient for
 * testing the sequence builder in isolation. The actual audio engine
 * (`engine.playSequence`) reads `duration`-stepped `NoteEvent[]`, so the
 * hook adapts this shape to a sequential `NoteEvent[]` before calling.
 */
export interface PlaybackEvent {
  note: NoteName
  octave: number
  duration: EventDuration
  /** Seconds from the start of the sequence. */
  time: number
  track: 'melody' | 'chord'
}

interface BuildSequenceInput {
  notes: MelodyNote[]
  chords: Chord[]
  tempoBpm: number
}

/**
 * Build a time-aligned playback sequence combining melody + chord-block
 * accompaniment. Pure function — testable without the audio engine.
 *
 * Time conversion: `position` is in beats; one beat = `60 / tempoBpm`
 * seconds. Each chord block fires at `chordIdx * BEATS_PER_CHORD` beats
 * and emits one `chord`-track event per chord note.
 */
export const buildPlaybackSequence = (input: BuildSequenceInput): PlaybackEvent[] => {
  const { notes, chords, tempoBpm } = input
  const beatSeconds = 60 / tempoBpm

  const melodyEvents: PlaybackEvent[] = notes.map(n => ({
    note: n.pitch,
    octave: n.octave,
    duration: n.duration,
    time: n.position * beatSeconds,
    track: 'melody',
  }))

  const chordEvents: PlaybackEvent[] = chords.flatMap((chord, idx) => {
    const beat = idx * BEATS_PER_CHORD
    const time = beat * beatSeconds
    const chordNotes = ChordLib.getNotes(chord)
    return chordNotes.map(n => ({
      note: n,
      octave: CHORD_VOICING_OCTAVE,
      duration: '1n' as EventDuration,
      time,
      track: 'chord' as const,
    }))
  })

  return [...melodyEvents, ...chordEvents].sort((a, b) => a.time - b.time)
}

export interface UseMelodyPlaybackProps {
  notes: MelodyNote[]
  chords: Chord[]
  tempoBpm: number
}

/**
 * Hook wrapper around `usePlayNotes.playNoteSequence`. Returns a `play`
 * callback that submits the melody to the audio engine.
 *
 * v1 limitation: the engine's `playSequence` walks events by `duration`
 * without honouring an absolute `time` offset. The hook plays the melody
 * notes in beat-order as a flat sequence; chord-backing playback lands in
 * a follow-up task that wires the engine's MIDI scheduler directly.
 */
export const useMelodyPlayback = (props: UseMelodyPlaybackProps) => {
  const { playNoteSequence } = usePlayNotes()
  const play = () => {
    const seq = buildPlaybackSequence(props)
    // v1: play melody only; chord backing requires a scheduler we don't expose yet.
    const noteEvents: NoteEvent[] = seq
      .filter(e => e.track === 'melody')
      .map(e => ({
        type: 'note' as const,
        note: e.note,
        octave: (e.octave as Octave) ?? (MELODY_OCTAVE_FALLBACK as Octave),
        duration: e.duration,
      }))
    playNoteSequence(noteEvents)
  }
  return { play }
}
